import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { PokerSiteId } from '@poker-stats/contracts';
import type { RootState } from '../../configureStore';
import {
  addMiningItems,
  changeMiningRoom,
  clearMiningQueue,
  disposeMiningQueue,
  initializeMiningQueue,
  startMiningUpload,
} from './reducer';
import { acceptsMiningFile, type MiningUploadFile, type MiningUploadItem } from './types';

export function useMiningUpload() {
  const dispatch = useDispatch();
  const queueId = useRef(nanoid()).current;
  const files = useRef(new Map<string, File>());
  const controller = useRef<AbortController>();
  const launching = useRef(false);
  const state = useSelector((root: RootState) => root.miningUpload);
  const currentState = useRef(state);
  currentState.current = state;
  const items = state.queueId === queueId ? state.items : [];
  const isUploading = state.queueId === queueId && state.isUploading;

  useEffect(() => {
    if (!isUploading) launching.current = false;
  }, [isUploading]);

  useEffect(() => {
    dispatch(initializeMiningQueue(queueId));
    const fileReferences = files.current;
    const dispose = () => {
      controller.current?.abort();
      controller.current = undefined;
      launching.current = false;
      fileReferences.clear();
      dispatch(disposeMiningQueue(queueId));
    };
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) dispatch(initializeMiningQueue(queueId));
    };
    window.addEventListener('pagehide', dispose);
    window.addEventListener('pageshow', restore);
    return () => {
      window.removeEventListener('pagehide', dispose);
      window.removeEventListener('pageshow', restore);
      dispose();
    };
  }, [dispatch, queueId]);

  const setRoom = useCallback(
    (room: PokerSiteId) => {
      dispatch(changeMiningRoom({ queueId, room }));
    },
    [dispatch, queueId],
  );

  const addFiles = useCallback(
    (selected: File[]) => {
      const queue = currentState.current;
      if (queue.queueId !== queueId || queue.isUploading || launching.current) return;
      const added: MiningUploadItem[] = [];
      let skippedCount = 0;
      for (const file of selected) {
        if (!acceptsMiningFile(queue.room, file.name)) {
          skippedCount += 1;
          continue;
        }
        const id = nanoid();
        files.current.set(id, file);
        added.push({
          id,
          name: file.name,
          relativePath: file.webkitRelativePath || file.name,
          size: file.size,
          status: 'pending',
        });
      }
      dispatch(addMiningItems({ queueId, items: added, skippedCount }));
    },
    [dispatch, queueId],
  );

  const uploadItems = useCallback(
    (status: 'pending' | 'error') => {
      const queue = currentState.current;
      if (queue.queueId !== queueId || queue.isUploading || launching.current) return;
      const selected = queue.items.reduce<MiningUploadFile[]>((result, item) => {
        const file = files.current.get(item.id);
        if (item.status === status && file) result.push({ id: item.id, file });
        return result;
      }, []);
      if (!selected.length) return;
      launching.current = true;
      controller.current?.abort();
      controller.current = new AbortController();
      dispatch(
        startMiningUpload({
          queueId,
          batchId: nanoid(),
          room: queue.room,
          upload: { files: selected, signal: controller.current.signal },
        }),
      );
    },
    [dispatch, queueId],
  );

  const startUpload = useCallback(() => uploadItems('pending'), [uploadItems]);
  const retryErrors = useCallback(() => uploadItems('error'), [uploadItems]);
  const clearQueue = useCallback(() => {
    const queue = currentState.current;
    if (queue.queueId !== queueId || queue.isUploading || launching.current) return;
    files.current.clear();
    dispatch(clearMiningQueue(queueId));
  }, [dispatch, queueId]);

  const room: PokerSiteId = state.queueId === queueId ? state.room : 1;

  return {
    room,
    setRoom,
    items,
    skippedCount: state.queueId === queueId ? state.skippedCount : 0,
    isUploading,
    addFiles,
    startUpload,
    retryErrors,
    clearQueue,
  };
}
